/**
 * The shapes a broadcast can take.
 *
 * ## Templates, not a compositor
 *
 * OBS gives you an empty canvas and a layer list, and the price is that every
 * host has to be a designer before they can be a host. Nobody's first broadcast
 * should be spent dragging a webcam rectangle around until it looks acceptable.
 *
 * So the studio has four shapes and the host picks one. Each decides where the
 * camera goes, where the now-playing card goes and how big the artwork is - the
 * decisions that make a broadcast look composed rather than assembled. What the
 * host still controls is the part that is actually theirs: which slots are on,
 * which camera, and the colour behind it all.
 *
 * The constraint is deliberate and it is the feature. A studio that could
 * express any layout would also express every bad one.
 *
 * ## Normalised coordinates
 *
 * Every slot is a fraction of the canvas rather than a pixel box, so the same
 * layout composes at 720p, at the preview's size, and at whatever resolution a
 * future encoder wants. A layout table in pixels is a layout table that has to
 * be rewritten the first time the output size changes.
 */

/** A rectangle, in fractions of the canvas. */
export interface Slot {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface StudioLayout {
  id: string;
  label: string;
  /** What this shape is *for*, in the host's terms. */
  hint: string;

  camera: Slot | null;
  /** Circular masks read as an inset; rectangles read as the frame. */
  cameraShape: 'rect' | 'circle';

  card: Slot | null;
  /**
   * `bar` is a lower third over the camera; `panel` is a column beside it;
   * `hero` is the artwork as the subject rather than a label on something else.
   */
  cardStyle: 'bar' | 'panel' | 'hero';

  brand: Slot | null;
}

/*
 * Named, and referenced by name below.
 *
 * `LAYOUTS[0]` would be `StudioLayout | undefined` under
 * `noUncheckedIndexedAccess` - correct in general, and noise here where the
 * array is a literal three lines down. Naming the default says what it is
 * rather than where it happens to sit.
 */
const CAMERA_LAYOUT: StudioLayout = {
  id: 'camera',
  label: 'Camera',
  hint: 'You fill the frame, with the track along the bottom.',
  camera: { x: 0, y: 0, w: 1, h: 1 },
  cameraShape: 'rect',
  // A lower third rather than a corner: it is the one place a caption can sit
  // over a moving picture without covering the face that is moving.
  card: { x: 0.04, y: 0.76, w: 0.62, h: 0.16 },
  cardStyle: 'bar',
  brand: { x: 0.04, y: 0.06, w: 0.4, h: 0.09 },
};

export const LAYOUTS: StudioLayout[] = [
  CAMERA_LAYOUT,
  {
    id: 'split',
    label: 'Split',
    hint: 'You on one side, the track on the other.',
    camera: { x: 0.04, y: 0.1, w: 0.5, h: 0.8 },
    cameraShape: 'rect',
    card: { x: 0.58, y: 0.1, w: 0.38, h: 0.8 },
    cardStyle: 'panel',
    brand: { x: 0.58, y: 0.9, w: 0.38, h: 0.07 },
  },
  {
    id: 'pip',
    label: 'Artwork with camera',
    hint: 'The record is the picture; you are in the corner.',
    // Bottom right, and small. Anything larger competes with the artwork it is
    // supposed to be sitting beside.
    camera: { x: 0.75, y: 0.66, w: 0.2, h: 0.28 },
    cameraShape: 'circle',
    card: { x: 0.1, y: 0.1, w: 0.8, h: 0.8 },
    cardStyle: 'hero',
    brand: { x: 0.06, y: 0.9, w: 0.4, h: 0.07 },
  },
  {
    id: 'art',
    label: 'Artwork only',
    hint: 'No camera. For a set you want to run without being on it.',
    camera: null,
    cameraShape: 'rect',
    card: { x: 0.1, y: 0.08, w: 0.8, h: 0.8 },
    cardStyle: 'hero',
    brand: { x: 0.06, y: 0.9, w: 0.5, h: 0.07 },
  },
];

export const DEFAULT_LAYOUT = CAMERA_LAYOUT;

export function layoutById(id: string): StudioLayout {
  return LAYOUTS.find((l) => l.id === id) ?? DEFAULT_LAYOUT;
}

/**
 * The output size. 720p at 30fps.
 *
 * Not 1080p: the encode happens on the host's machine while it is also decoding
 * the music, and the difference between the two on a laptop is a fan that never
 * stops. Not 480p either - a broadcast that looks like 2011 is one people
 * assume is broken.
 */
export const CANVAS_WIDTH = 1280;
export const CANVAS_HEIGHT = 720;
export const CANVAS_FPS = 30;
